import { useLayoutEffect, useRef, useState } from 'react'
import { scaleBand, scaleLinear } from 'd3-scale'
import { useTheme } from '../../app/ThemeProvider'
import { fixedDecimals } from '../../platform/fixedDecimals'
import { Money } from '../../platform/primitives/Money'
import type { ChartType } from './reportSpec'
import { stackLayout, type ChartSeries } from './reportView'
import './reports.css'

/**
 * The Reports chart (M8 P3, P7, P8): series × periods in three readings. Hand-rolled SVG on
 * `d3-scale`; every fill and stroke is a class in `reports.css` (rules 1 and 2).
 *
 * It draws what it is given. The cells are the server's; the only arithmetic is `stackLayout`'s pixel
 * offsets. A negative cell is drawn below the zero line (B5). The bucket still filling is outlined and
 * labelled "so far" (B6), so colour is never the only signal. The chart has no selection: it is a
 * `role="img"` with one sentence, and the table beside it is the accessible reading of the same data (P8).
 * `data-chart` sits on the marks only.
 */

type Props = {
  series: ChartSeries[]
  /** `columnLabel` per period. */
  labels: string[]
  /** `currentIndex`. */
  current: number | null
  type: ChartType
  /** For the axis `Money` only. */
  currency: string
  /** The accessible sentence. */
  label: string
}

/** Plot height, px. */
const PLOT_HEIGHT = 200
const INSET = 6
/** The narrowest a period column may get; past it the plot scrolls inside its viewport. */
const MIN_PITCH = 28
const LABEL_ROWS = 34
const FALLBACK_WIDTH = 640
const AXIS_TICKS = 4
/**
 * The separator between stacked segments, px, per theme. Mirrors `--series-gap` in tokens.css (0 in
 * light, 1px in dark) — a test holds the two together. It is geometry, not a stroke: a stroke overlays the
 * fill and swallows a small segment, so the gap is cut from the base of the upper segment instead.
 */
export const SERIES_GAP = { light: 0, dark: 1 } as const
/** A segment shorter than this keeps its full geometry and gets no gap, so a small value is never lost. */
export const GAP_MIN_SEGMENT = 4
const num = (n: number) => fixedDecimals(n, 2)

export function ReportChart({ series, labels, current, type, currency, label }: Props) {
  const viewport = useRef<HTMLDivElement>(null)
  const [measured, setMeasured] = useState(0)
  useLayoutEffect(() => {
    const el = viewport.current
    if (el === null || typeof ResizeObserver === 'undefined') return
    setMeasured(el.clientWidth)
    const observer = new ResizeObserver(() => setMeasured(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const theme = useTheme().resolved
  const gap = SERIES_GAP[theme]
  const n = labels.length
  const width = Math.max(measured > 0 ? measured : FALLBACK_WIDTH, n * MIN_PITCH)

  const flat = (i: number) => series.map((s) => s.cents[i] ?? 0)
  const reach = labels.flatMap((_, i) =>
    type === 'stacked' ? stackLayout(flat(i), (v) => v).map((r) => r.y1) : flat(i),
  )
  const lo = Math.min(0, ...reach)
  const hi = Math.max(0, ...reach)
  const y = scaleLinear()
    .domain(lo === hi ? [0, 1] : [lo, hi])
    .nice(AXIS_TICKS)
    .range([PLOT_HEIGHT - INSET, INSET])
  const x = scaleBand<number>()
    .domain(labels.map((_, i) => i))
    .range([0, width])
    .paddingInner(0.2)
    .paddingOuter(0.1)

  const zero = y(0)
  const inner = scaleBand<number>()
    .domain(series.map((_, k) => k))
    .range([0, x.bandwidth()])
    .paddingInner(0.08)
  const ticks = y.ticks(AXIS_TICKS)

  const rects: { key: string; cls: string; x: number; y: number; w: number; h: number }[] = []
  if (type === 'stacked') {
    labels.forEach((_, i) => {
      const values = flat(i)
      // Last drawn segment per direction (up, down): the one a new segment sits on.
      const below: [number | null, number | null] = [null, null]
      stackLayout(values, y).forEach((seg, k) => {
        const s = series[k]
        if (s === undefined) return
        const h = Math.abs(seg.y0 - seg.y1)
        const dir = (values[k] ?? 0) >= 0 ? 0 : 1
        const under = below[dir]
        if (h > 0) below[dir] = h
        const cut = gap > 0 && h >= GAP_MIN_SEGMENT && under !== null && under >= GAP_MIN_SEGMENT ? gap : 0
        const top = Math.min(seg.y0, seg.y1)
        rects.push({
          key: `${s.id}-${i}`,
          cls: `rp-chart__s${s.index}`,
          x: x(i) ?? 0,
          // The base is the edge nearest the zero line: the bottom of an upward segment, the top of a downward one.
          y: dir === 0 ? top : top + cut,
          w: x.bandwidth(),
          h: h - cut,
        })
      })
    })
  } else if (type === 'grouped') {
    labels.forEach((_, i) => {
      series.forEach((s, k) => {
        const top = y(s.cents[i] ?? 0)
        rects.push({
          key: `${s.id}-${i}`,
          cls: `rp-chart__s${s.index}`,
          x: (x(i) ?? 0) + (inner(k) ?? 0),
          y: Math.min(top, zero),
          w: inner.bandwidth(),
          h: Math.abs(top - zero),
        })
      })
    })
  }

  return (
    <div className="rp-chart">
      <div className="rp-chart__body">
        <div className="rp-chart__axis" style={{ height: PLOT_HEIGHT }}>
          {ticks.map((t) => (
            <span key={t} className="rp-chart__tick" style={{ top: y(t) }}>
              <Money cents={Math.round(t)} currency={currency} display="compact" symbol={false} />
            </span>
          ))}
        </div>
        <div className="rp-chart__viewport" ref={viewport}>
          <svg
            className="rp-chart__svg"
            role="img"
            aria-label={label}
            width={width}
            height={PLOT_HEIGHT + LABEL_ROWS}
            viewBox={`0 0 ${num(width)} ${PLOT_HEIGHT + LABEL_ROWS}`}
          >
            <line className="rp-chart__zero" x1={0} x2={width} y1={zero} y2={zero} />
            {current !== null && (
              <rect
                className="rp-chart__current"
                x={(x(current) ?? 0) - 3}
                y={INSET - 3}
                width={x.bandwidth() + 6}
                height={PLOT_HEIGHT - 2 * INSET + 6}
                rx={6}
              />
            )}
            {rects.map((r) => (
              <rect key={r.key} className={r.cls} data-chart="bar" x={num(r.x)} y={num(r.y)} width={num(r.w)} height={num(r.h)} />
            ))}
            {type === 'line' &&
              series.map((s) => {
                const pts = labels.map((_, i) => [(x(i) ?? 0) + x.bandwidth() / 2, y(s.cents[i] ?? 0)] as const)
                const d = pts.map(([px, py], i) => `${i === 0 ? 'M' : 'L'} ${num(px)} ${num(py)}`).join(' ')
                return (
                  <g key={s.id}>
                    <path className={`rp-chart__line rp-chart__line--s${s.index}`} data-chart="line" d={d} />
                    {pts.map(([px, py], i) => (
                      <circle
                        key={i}
                        className={`rp-chart__dot rp-chart__s${s.index}`}
                        data-chart="point"
                        cx={num(px)}
                        cy={num(py)}
                        r={3}
                      />
                    ))}
                  </g>
                )
              })}
            {labels.map((l, i) => {
              const cx = (x(i) ?? 0) + x.bandwidth() / 2
              return (
                <g key={i}>
                  <text className="rp-chart__label" x={num(cx)} y={PLOT_HEIGHT + 14} textAnchor="middle">
                    {l}
                  </text>
                  {i === current && (
                    <text className="rp-chart__label rp-chart__label--partial" x={num(cx)} y={PLOT_HEIGHT + 28} textAnchor="middle">
                      so far
                    </text>
                  )}
                </g>
              )
            })}
          </svg>
        </div>
      </div>
      <ul className="rp-legend">
        {series.map((s) => (
          <li key={s.id} className="rp-legend__item">
            <span className={`rp-legend__swatch rp-legend__swatch--s${s.index}`} aria-hidden="true" />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
